import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useROStore } from "../context/ROContext";
import { useAuth } from "../context/AuthContext";
import { useROUnread } from "../context/ROUnreadContext";
import ROActivityDrawer from "../components/ro/ROActivityDrawer";
import {
  RO_LIFECYCLE,
  RO_STATUS,
  PARTS_STATUS,
  PARTS_ACTION_STATUS,
} from "../constants/roStatus";

const NO_ETA = "NO_ETA";

/* =========================
   Helpers
========================= */
function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function nowISO() {
  return new Date().toISOString();
}

function pillStyle({
  bg = "#fff",
  border = "#e5e7eb",
  color = "#111827",
} = {}) {
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 900,
    border: `1px solid ${border}`,
    background: bg,
    color,
    whiteSpace: "nowrap",
    lineHeight: 1.2,
  };
}

function getTransportText(v) {
  const s = String(v || "").toUpperCase();
  if (s === "WAITER") return "Waiter";
  if (s === "LOANER") return "Loaner";
  if (s === "SHUTTLE") return "Shuttle";
  if (s === "OTHER") return "Other";
  return s ? s : "";
}

function getTag(ro) {
  const raw =
    ro?.tagNumber ??
    ro?.tag_number ??
    ro?.tag ??
    ro?.keyTag ??
    ro?.key_tag ??
    ro?.tagId ??
    ro?.tag_id ??
    "";
  return safeString(raw).trim();
}

function noteTextFromValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((n) => {
        if (typeof n === "string") return n;
        if (n && typeof n === "object") {
          return safeString(
            n.text || n.body || n.note || n.message || n.value || ""
          ).trim();
        }
        return "";
      })
      .filter(Boolean)
      .join("\n\n");
  }

  if (value && typeof value === "object") {
    return safeString(
      value.text || value.body || value.note || value.message || value.value || ""
    ).trim();
  }

  const raw = safeString(value).trim();
  return raw === "[object Object]" ? "" : raw;
}

function notePreview(notes) {
  const text = noteTextFromValue(notes);
  if (!text) return "";

  const lines = text
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

  return lines[lines.length - 1] || "";
}

function statusUpper(v) {
  return safeString(v).trim().toUpperCase();
}

function prettyEnum(v, fallback = "—") {
  const raw = safeString(v).trim();
  if (!raw) return fallback;

  return raw
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function compactDate(iso) {
  if (!iso) return "—";

  const raw = safeString(iso).trim();
  if (!raw) return "—";
  if (raw.toUpperCase() === NO_ETA) return "No ETA";

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw || "—";

  return d.toLocaleString([], {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const DATE_FILTERS = Object.freeze({
  TODAY: "TODAY",
  YESTERDAY: "YESTERDAY",
  THIS_WEEK: "THIS_WEEK",
  THIS_MONTH: "THIS_MONTH",
  CUSTOM: "CUSTOM",
});

function startOfLocalDay(input = new Date()) {
  const d = input instanceof Date ? input : new Date(input);
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return new Date(safe.getFullYear(), safe.getMonth(), safe.getDate());
}

function addDays(input, days) {
  const d = new Date(input);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfLocalWeekMonday(input = new Date()) {
  const d = startOfLocalDay(input);
  const day = d.getDay();
  return addDays(d, day === 0 ? -6 : 1 - day);
}

function dateInputValue(input = new Date()) {
  const d = input instanceof Date ? input : new Date(input);
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  const pad = (n) => String(n).padStart(2, "0");
  return `${safe.getFullYear()}-${pad(safe.getMonth() + 1)}-${pad(safe.getDate())}`;
}

function localDateFromInput(value) {
  const [year, month, day] = safeString(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function getPartsDateWindow(filter, customFrom, customTo) {
  const todayStart = startOfLocalDay();
  const tomorrowStart = addDays(todayStart, 1);

  if (filter === DATE_FILTERS.YESTERDAY) {
    return {
      start: addDays(todayStart, -1),
      end: todayStart,
      label: "Yesterday",
    };
  }

  if (filter === DATE_FILTERS.THIS_WEEK) {
    return {
      start: startOfLocalWeekMonday(),
      end: tomorrowStart,
      label: "This Week",
    };
  }

  if (filter === DATE_FILTERS.THIS_MONTH) {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: tomorrowStart,
      label: "This Month",
    };
  }

  if (filter === DATE_FILTERS.CUSTOM) {
    const start = localDateFromInput(customFrom);
    const toStart = localDateFromInput(customTo);
    return {
      start,
      end: toStart ? addDays(toStart, 1) : null,
      label: customFrom && customTo ? `${customFrom} to ${customTo}` : "Custom Range",
    };
  }

  return {
    start: todayStart,
    end: tomorrowStart,
    label: "Today",
  };
}

function isWithinDateWindowMs(ms, dateWindow) {
  if (!Number.isFinite(ms) || ms <= 0) return false;

  const start = dateWindow?.start?.getTime?.();
  const end = dateWindow?.end?.getTime?.();

  if (!Number.isFinite(start) || !Number.isFinite(end)) return true;

  return ms >= start && ms < end;
}

function partsActionedDateLabel(ro) {
  const t = getPartsActionSortTime(ro);
  if (!Number.isFinite(t) || t <= 0) return "—";
  return compactDate(new Date(t).toISOString());
}

function toMs(value, fallback = 0) {
  const d = value ? new Date(value) : null;
  const t = d ? d.getTime() : NaN;
  return Number.isFinite(t) ? t : fallback;
}

function getNewestHistoryTime(ro, eventTypes = []) {
  const wanted = new Set(eventTypes.map((x) => safeString(x).toUpperCase()));
  const events = Array.isArray(ro?.historyEvents) ? ro.historyEvents : [];

  let newest = 0;

  events.forEach((evt) => {
    const type = safeString(evt?.type || evt?.eventType).toUpperCase();
    if (wanted.size && !wanted.has(type)) return;

    const t = toMs(evt?.at || evt?.timestamp || evt?.createdAt || evt?.ts, 0);
    if (t > newest) newest = t;
  });

  return newest;
}

function getPartsActionSortTime(ro) {
  const historyTime = getNewestHistoryTime(ro, [
    "RO_PARTS_STATUS_CHANGED",
    "RO_PARTS_ACTION_STATUS_CHANGED",
    "RO_PARTS_QUOTE_PROVIDED",
    "RO_PARTS_REOPENED",
  ]);

  return Math.max(
    historyTime,
    toMs(ro?.partsActionedAt, 0),
    toMs(ro?.partsStatusUpdatedAt, 0),
    toMs(ro?.updatedAt, 0),
    toMs(ro?.timestamps?.updatedAt, 0),
    toMs(ro?.timestamps?.partsActionedAt, 0),
    toMs(ro?.createdAt, 0)
  );
}

function getOpenRequestSortTime(ro) {
  const historyTime = getNewestHistoryTime(ro, [
    "RO_PARTS_REQUESTED",
    "RO_CREATED",
    "RO_IMPORTED",
  ]);

  const fallback = Number.POSITIVE_INFINITY;

  const candidates = [
    historyTime || null,
    ro?.partsActionRequestedAt,
    ro?.createdAt,
    ro?.timestamps?.createdAt,
    ro?.updatedAt,
  ];

  for (const value of candidates) {
    const t = toMs(value, NaN);
    if (Number.isFinite(t)) return t;
  }

  return fallback;
}

/* -------------------------
   Lifecycle
-------------------------- */
function getLifecycle(ro, store) {
  if (typeof store?.getLifecycle === "function") {
    return String(store.getLifecycle(ro) || "").toUpperCase();
  }
  return String(ro?.lifecycle || ro?.lifeCycle || "").toUpperCase();
}

function normalizeLifecycle(lc, status) {
  const s = String(lc || "").toUpperCase();

  if (s === "WAITING_ON_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_ASSIGNMENT") return RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
  if (s === "UNASSIGNED") return "UNASSIGNED";
  if (s === "IN_PROGRESS") return RO_LIFECYCLE.IN_PROGRESS;
  if (s === "COMPLETED") return RO_LIFECYCLE.COMPLETED;
  if (s === "OPEN") return RO_LIFECYCLE.OPEN;

  const st = String(status || "").toUpperCase();

  if (!s) {
    if (st === String(RO_STATUS.COMPLETED).toUpperCase()) {
      return RO_LIFECYCLE.COMPLETED;
    }
    if (st === String(RO_STATUS.ARCHIVED).toUpperCase()) return "ARCHIVED";
    if (st === "WAITING_ON_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
    if (st === "IN_PROGRESS") return RO_LIFECYCLE.IN_PROGRESS;
    if (st === "ASSIGNED") return RO_LIFECYCLE.OPEN;
    return RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
  }

  return s;
}

function lifecycleLabel(ro, store) {
  const st = String(ro?.status || "").toUpperCase();
  if (st === String(RO_STATUS.ARCHIVED).toUpperCase()) return "ARCHIVED";

  const lc = store
    ? getLifecycle(ro, store)
    : normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle, ro?.status);

  if (
    lc === String(RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT).toUpperCase() ||
    lc === "UNASSIGNED"
  ) {
    return "UNASSIGNED";
  }

  if (lc === String(RO_LIFECYCLE.WAITING_FOR_PARTS).toUpperCase()) {
    return "WAITING PARTS";
  }

  if (lc === String(RO_LIFECYCLE.IN_PROGRESS).toUpperCase()) {
    return "IN PROGRESS";
  }

  if (lc === String(RO_LIFECYCLE.COMPLETED).toUpperCase()) return "COMPLETED";
  if (lc === "ARCHIVED") return "ARCHIVED";
  if (lc === String(RO_LIFECYCLE.OPEN).toUpperCase()) return "OPEN";

  return String(lc || "OPEN").replaceAll("_", " ");
}

function lifecyclePill(label) {
  if (label === "UNASSIGNED") {
    return {
      text: "UNASSIGNED",
      style: pillStyle({
        bg: "#dcfce7",
        border: "#86efac",
        color: "#166534",
      }),
    };
  }

  if (label === "WAITING PARTS") {
    return {
      text: "WAITING PARTS",
      style: pillStyle({
        bg: "#ffedd5",
        border: "#fdba74",
        color: "#9a3412",
      }),
    };
  }

  if (label === "IN PROGRESS") {
    return {
      text: "IN PROGRESS",
      style: pillStyle({
        bg: "#dbeafe",
        border: "#93c5fd",
        color: "#1e40af",
      }),
    };
  }

  if (label === "OPEN") {
    return {
      text: "OPEN",
      style: pillStyle({
        bg: "#dbeafe",
        border: "#93c5fd",
        color: "#1e40af",
      }),
    };
  }

  if (label === "COMPLETED") {
    return {
      text: "COMPLETED",
      style: pillStyle({
        bg: "#f1f5f9",
        border: "#cbd5e1",
        color: "#334155",
      }),
    };
  }

  if (label === "ARCHIVED") {
    return {
      text: "ARCHIVED",
      style: pillStyle({
        bg: "#e5e7eb",
        border: "#cbd5e1",
        color: "#334155",
      }),
    };
  }

  return {
    text: label,
    style: pillStyle({
      bg: "#f8fafc",
      border: "#e5e7eb",
      color: "#334155",
    }),
  };
}

/* -------------------------
   Request/status pills
-------------------------- */
function requestPill(kind) {
  const k = String(kind || "").toUpperCase();

  if (k === "NEED_PARTS") {
    return {
      text: "Parts Needed",
      style: pillStyle({
        bg: "#dbeafe",
        border: "#93c5fd",
        color: "#1e40af",
      }),
    };
  }

  if (k === "NEED_PRICE") {
    return {
      text: "Quote Requested",
      style: pillStyle({
        bg: "#ffedd5",
        border: "#fdba74",
        color: "#9a3412",
      }),
    };
  }

  return null;
}

function partsStatusLabel(ps) {
  const s = String(ps || "").toUpperCase();
  if (s === "PULLED") return "Pulled";
  if (s === "ORDERED") return "Ordered";
  if (s === "ARRIVED") return "Arrived";
  if (s === "QUOTED") return "Quoted";
  if (s === "NONE") return "—";
  return s ? prettyEnum(s) : "—";
}

function partsStatusPill(ps) {
  const s = String(ps || "").toUpperCase();

  if (s === "PULLED") {
    return {
      text: "Pulled",
      style: pillStyle({
        bg: "#dcfce7",
        border: "#86efac",
        color: "#166534",
      }),
    };
  }

  if (s === "ORDERED") {
    return {
      text: "Ordered",
      style: pillStyle({
        bg: "#ffedd5",
        border: "#fdba74",
        color: "#9a3412",
      }),
    };
  }

  if (s === "ARRIVED") {
    return {
      text: "Arrived",
      style: pillStyle({
        bg: "#dbeafe",
        border: "#93c5fd",
        color: "#1e40af",
      }),
    };
  }

  if (s === "QUOTED") {
    return {
      text: "Quoted",
      style: pillStyle({
        bg: "#ede9fe",
        border: "#c4b5fd",
        color: "#5b21b6",
      }),
    };
  }

  return {
    text: partsStatusLabel(s),
    style: pillStyle({
      bg: "#f8fafc",
      border: "#e5e7eb",
      color: "#334155",
    }),
  };
}

function actionStatusPill(actionStatus) {
  const s = statusUpper(actionStatus);

  if (s === "PRE_PULLED" || s === "PREPULLED") {
    return {
      text: "Pre-Pulled",
      style: pillStyle({
        bg: "#dcfce7",
        border: "#86efac",
        color: "#166534",
      }),
    };
  }

  if (s === "NO_ACTION") {
    return {
      text: "No Action",
      style: pillStyle({
        bg: "#e5e7eb",
        border: "#cbd5e1",
        color: "#334155",
      }),
    };
  }

  return null;
}

function attentionPill(text) {
  return {
    text,
    style: pillStyle({
      bg: "#fee2e2",
      border: "#fca5a5",
      color: "#991b1b",
    }),
  };
}

function pickActiveSource({ wipROs, computedWip, isLoading }) {
  if (Array.isArray(wipROs)) {
    if (
      isLoading &&
      wipROs.length === 0 &&
      Array.isArray(computedWip) &&
      computedWip.length > 0
    ) {
      return computedWip;
    }
    return wipROs;
  }

  return computedWip;
}

function isWipROFallback(ro, store) {
  const st = String(ro?.status || "").toUpperCase();
  if (st === String(RO_STATUS.ARCHIVED).toUpperCase()) return false;

  const lcRaw = store
    ? getLifecycle(ro, store)
    : String(
        normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle, ro?.status)
      ).toUpperCase();

  if (lcRaw === String(RO_LIFECYCLE.COMPLETED).toUpperCase()) return false;
  if (st === String(RO_STATUS.COMPLETED).toUpperCase()) return false;

  return true;
}

function isClosedForPartsQueue(ro) {
  const status = statusUpper(ro?.status);
  const lifecycle = statusUpper(ro?.lifecycle || ro?.lifeCycle);
  const pickupStatus = statusUpper(
    ro?.customerPickupStatus || ro?.customer_pickup_status
  );
  const advisorAction = statusUpper(
    ro?.advisorActionStatus || ro?.advisor_action_status
  );

  if (status === "ARCHIVED" || lifecycle === "ARCHIVED") return true;

  if (status === "VEHICLE_GONE" || lifecycle === "VEHICLE_GONE") return true;

  if (pickupStatus === "VEHICLE_GONE" || advisorAction === "VEHICLE_GONE") {
    return true;
  }

  if (ro?.vehicleGone || ro?.vehicle_gone || ro?.pickedUp || ro?.picked_up) {
    return true;
  }

  return false;
}

function isActionedPartsStatus(status) {
  const s = statusUpper(status);
  return (
    s === "PULLED" ||
    s === "ORDERED" ||
    s === "ARRIVED" ||
    s === "QUOTED"
  );
}

function isActionedPartsActionStatus(status) {
  const s = statusUpper(status);

  if (!s) return false;
  if (s === "NONE" || s === "CLEAR") return false;
  if (s === "REVIEW" || s === "OPEN" || s === "REOPEN") return false;

  return true;
}

function isActionedPartsRO(partsStatus, actionStatus) {
  return isActionedPartsStatus(partsStatus) || isActionedPartsActionStatus(actionStatus);
}

function hasActivePartsRequest(ro, effectivePartsStatus, effectiveActionStatus) {
  const requestType = statusUpper(
    ro?.partsRequestType || ro?.parts_request_type || ""
  );

  if (requestType !== "NEED_PARTS" && requestType !== "NEED_PRICE") {
    return false;
  }

  const partsStatus = statusUpper(effectivePartsStatus);
  const actionStatus = statusUpper(effectiveActionStatus);

  const waitingForReview =
    !actionStatus ||
    actionStatus === "NONE" ||
    actionStatus === "REVIEW" ||
    actionStatus === "OPEN" ||
    actionStatus === "REOPEN";

  const noConcretePartsAction =
    !partsStatus || partsStatus === "NONE" || partsStatus === "NEEDED";

  return waitingForReview && noConcretePartsAction;
}

function formatROTitle(ro) {
  const roNumber = safeString(ro?.roNumber || ro?.ro_number || "—").trim() || "—";
  return `RO #${roNumber}`;
}

/* =========================
   Shared styles
========================= */
const pageWrap = {
  padding: 12,
  display: "grid",
  gap: 10,
  width: "100%",
  maxWidth: 1500,
  margin: "0 auto",
  boxSizing: "border-box",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
  marginBottom: 2,
};

const headerControls = {
  marginLeft: "auto",
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};

const eyebrowStyle = {
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(15,23,42,0.52)",
};

const pageTitleStyle = {
  margin: 0,
  fontSize: 22,
  lineHeight: 1.02,
  fontWeight: 700,
  letterSpacing: "-0.035em",
  color: "rgba(15,23,42,0.94)",
};

const pageSubtitleStyle = {
  margin: "4px 0 0",
  color: "rgba(15,23,42,0.58)",
  fontSize: 11.5,
  lineHeight: 1.32,
};

const metricGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 8,
};

const metricCardBase = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: "0.56rem 0.65rem",
  minHeight: 62,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.035)",
};

const metricLabelStyle = {
  fontSize: "0.64rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#64748b",
};

const metricValueStyle = {
  marginTop: "0.22rem",
  fontSize: "1.22rem",
  lineHeight: 1,
  fontWeight: 750,
  color: "#0f172a",
};

const metricSubStyle = {
  marginTop: "0.28rem",
  fontSize: "0.72rem",
  color: "#64748b",
  lineHeight: 1.25,
};

const toolbarStyle = {
  background: "#ffffff",
  borderRadius: 12,
  padding: "0.62rem 0.7rem",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.045)",
  border: "1px solid #e5e7eb",
  display: "grid",
  gap: 6,
};

const queueColumnsStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 2.9fr) minmax(300px, 0.95fr)",
  gap: 10,
  alignItems: "start",
};

const columnCard = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: "0.62rem 0.7rem",
  minWidth: 0,
  overflow: "hidden",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.045)",
};

const grid = {
  display: "grid",
  gap: 7,
};

const cardBase = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: "0.5rem 0.55rem",
  minWidth: 0,
};

const sectionHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap",
  marginBottom: 7,
};

const sectionTitleStyle = {
  fontSize: "0.68rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#334155",
};

const sectionSubStyle = {
  color: "#64748b",
  fontSize: "0.72rem",
  lineHeight: 1.25,
  marginTop: 2,
};

const selectStyle = {
  height: 32,
  padding: "0 0.62rem",
  borderRadius: 9,
  border: "1px solid #d1d5db",
  background: "#ffffff",
  minWidth: 150,
  fontSize: "0.76rem",
  fontWeight: 700,
  fontFamily: "inherit",
};

const dateInputStyle = {
  height: 32,
  padding: "0 0.62rem",
  borderRadius: 9,
  border: "1px solid #d1d5db",
  background: "#ffffff",
  fontSize: "0.76rem",
  fontWeight: 700,
  fontFamily: "inherit",
};

const searchInputStyle = {
  height: 32,
  padding: "0 0.62rem",
  borderRadius: 9,
  border: "1px solid #d1d5db",
  width: "100%",
  fontSize: "0.76rem",
  background: "#ffffff",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const emptyStateStyle = {
  padding: 12,
  border: "1px dashed #cbd5e1",
  borderRadius: 10,
  color: "#64748b",
  fontSize: "0.76rem",
  background: "#ffffff",
};

function metricCardStyle(tone = "neutral") {
  if (tone === "danger") {
    return {
      ...metricCardBase,
      borderColor: "#fecaca",
      background: "linear-gradient(180deg, #fffafa 0%, #ffffff 100%)",
    };
  }

  if (tone === "warn") {
    return {
      ...metricCardBase,
      borderColor: "#fed7aa",
      background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
    };
  }

  if (tone === "good") {
    return {
      ...metricCardBase,
      borderColor: "#bbf7d0",
      background: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)",
    };
  }

  return metricCardBase;
}

function queueCountPillStyle(tone) {
  if (tone === "open") {
    return {
      padding: "4px 8px",
      borderRadius: 999,
      background: "#fff7ed",
      color: "#9a3412",
      fontWeight: 800,
      fontSize: "0.68rem",
      border: "1px solid #fed7aa",
      lineHeight: 1,
    };
  }

  return {
    padding: "4px 8px",
    borderRadius: 999,
    background: "#f0fdf4",
    color: "#166534",
    fontWeight: 800,
    fontSize: "0.68rem",
    border: "1px solid #bbf7d0",
    lineHeight: 1,
  };
}

function actionButtonStyle(tone) {
  const base = {
    minHeight: 28,
    padding: "0 0.48rem",
    borderRadius: 8,
    fontWeight: 700,
    cursor: "pointer",
    fontSize: "0.68rem",
    lineHeight: 1,
    whiteSpace: "nowrap",
  };

  if (tone === "pulled") {
    return {
      ...base,
      border: "1px solid #86efac",
      background: "#dcfce7",
      color: "#166534",
    };
  }

  if (tone === "ordered") {
    return {
      ...base,
      border: "1px solid #fdba74",
      background: "#ffedd5",
      color: "#9a3412",
    };
  }

  if (tone === "arrived") {
    return {
      ...base,
      border: "1px solid #93c5fd",
      background: "#dbeafe",
      color: "#1e40af",
    };
  }

  if (tone === "quoted") {
    return {
      ...base,
      border: "1px solid #c4b5fd",
      background: "#ede9fe",
      color: "#5b21b6",
    };
  }

  if (tone === "reopen") {
    return {
      ...base,
      border: "1px solid #d1d5db",
      background: "#ffffff",
      color: "#374151",
    };
  }

  return {
    ...base,
    border: "1px solid #d1d5db",
    background: "#ffffff",
    color: "#111827",
  };
}

function reviewAgeLabel(ro) {
  const t = getOpenRequestSortTime(ro);
  if (!Number.isFinite(t) || t <= 0) return "—";

  const diff = Math.max(0, Date.now() - t);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  const remMinutes = minutes % 60;

  if (days > 0 && remHours > 0) return `${days}d ${remHours}h`;
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h ${remMinutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return "Just now";
}

function getOldestReviewLabel(openQueue) {
  if (!Array.isArray(openQueue) || openQueue.length === 0) return "—";

  const oldest = openQueue
    .map((ro) => getOpenRequestSortTime(ro))
    .filter((t) => Number.isFinite(t) && t > 0)
    .sort((a, b) => a - b)[0];

  if (!oldest) return "—";
  return reviewAgeLabel({ partsActionRequestedAt: new Date(oldest).toISOString() });
}

/* =========================
   Page
========================= */
export default function PartsPage() {
  const store = useROStore();
  const auth = useAuth();
  const {
    hasUnread,
    isHydrated: unreadHydrated,
    storeId: unreadStoreId,
  } = useROUnread();

  const {
    repairOrders,
    ros: rosAlt,
    wipROs,
    isLoading,
    currentUser,
    getLifecycle: getLifecycleFn,
    isWipRO: isWipROFn,
    getAttentionFlags,
    setPartsStatus: setPartsStatusFn,
    setPartsActionStatus: setPartsActionStatusFn,
    updateRO: updateROFn,
    refreshStoreData: refreshStoreDataFn,
    refreshROsOnly: refreshROsOnlyFn,
    setPartsRequestType,
    setPartsRequestNote,
  } = store || {};

  const ros = useMemo(() => {
    const a = Array.isArray(repairOrders) ? repairOrders : null;
    const b = Array.isArray(rosAlt) ? rosAlt : null;
    return a || b || [];
  }, [repairOrders, rosAlt]);

  const actorRole = safeString(currentUser?.role || "parts");
  const actorName = safeString(currentUser?.name || "Parts");

  const [search, setSearch] = useState("");
  const [flashByRO, setFlashByRO] = useState({});
  const [threadROId, setThreadROId] = useState(null);
  const currentUnreadStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId
  ).trim();
  const unreadStoreMatches =
    !!currentUnreadStoreId &&
    safeString(unreadStoreId).trim() === currentUnreadStoreId;
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.TODAY);
  const [customFromDate, setCustomFromDate] = useState(() => dateInputValue());
  const [customToDate, setCustomToDate] = useState(() => dateInputValue());
  const [hasFreshPartsLoad, setHasFreshPartsLoad] = useState(false);
  const [optimisticPartsStatusByRO, setOptimisticPartsStatusByRO] = useState({});
  const [optimisticActionStatusByRO, setOptimisticActionStatusByRO] = useState({});

  const getEffectivePartsStatus = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      if (!roId) return ro?.partsStatus;

      if (
        Object.prototype.hasOwnProperty.call(optimisticPartsStatusByRO, roId)
      ) {
        return optimisticPartsStatusByRO[roId];
      }

      return ro?.partsStatus;
    },
    [optimisticPartsStatusByRO]
  );

  const getEffectiveActionStatus = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      if (!roId) return ro?.partsActionStatus || PARTS_ACTION_STATUS.REVIEW;

      if (
        Object.prototype.hasOwnProperty.call(optimisticActionStatusByRO, roId)
      ) {
        return optimisticActionStatusByRO[roId];
      }

      return ro?.partsActionStatus || PARTS_ACTION_STATUS.REVIEW;
    },
    [optimisticActionStatusByRO]
  );

  const dateWindow = useMemo(
    () => getPartsDateWindow(dateFilter, customFromDate, customToDate),
    [dateFilter, customFromDate, customToDate]
  );

  const flash = useCallback((roId, msg) => {
    setFlashByRO((m) => ({ ...m, [roId]: msg }));

    window.setTimeout(() => {
      setFlashByRO((m) => {
        const next = { ...m };
        delete next[roId];
        return next;
      });
    }, 1600);
  }, []);

  useEffect(() => {
    let alive = true;
    let running = false;

    const showQueueSoon = window.setTimeout(() => {
      if (alive) setHasFreshPartsLoad(true);
    }, 700);

    const refresh = async () => {
      const refreshFn =
        typeof refreshROsOnlyFn === "function"
          ? refreshROsOnlyFn
          : refreshStoreDataFn;

      if (!alive || running || typeof refreshFn !== "function") return;

      running = true;

      try {
        await refreshFn();
      } catch {
        // keep the parts page usable if a background refresh fails
      } finally {
        running = false;

        if (alive) setHasFreshPartsLoad(true);
      }
    };

    refresh();

    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    }, 1500);

    const onFocus = () => refresh();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      alive = false;
      window.clearTimeout(showQueueSoon);
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshStoreDataFn, refreshROsOnlyFn]);

  const queueData = useMemo(() => {
    const q = search.trim().toLowerCase();

    const computedWip = Array.isArray(ros)
      ? ros.filter((r) =>
          typeof isWipROFn === "function"
            ? isWipROFn(r)
            : isWipROFallback(r, store)
        )
      : [];

    const activeSource = pickActiveSource({ wipROs, computedWip, isLoading });

    const openSortFn = (a, b) => {
      const waiterA =
        safeString(a?.transportType).toUpperCase() === "WAITER" ? 0 : 1;
      const waiterB =
        safeString(b?.transportType).toUpperCase() === "WAITER" ? 0 : 1;

      if (waiterA !== waiterB) return waiterA - waiterB;

      const promiseA = a?.promiseTimeISO
        ? new Date(a.promiseTimeISO).getTime()
        : Number.POSITIVE_INFINITY;

      const promiseB = b?.promiseTimeISO
        ? new Date(b.promiseTimeISO).getTime()
        : Number.POSITIVE_INFINITY;

      if (promiseA !== promiseB) return promiseA - promiseB;

      return getOpenRequestSortTime(a) - getOpenRequestSortTime(b);
    };

    const actionedSortFn = (a, b) => {
      return getPartsActionSortTime(b) - getPartsActionSortTime(a);
    };

    const filtered = (activeSource || []).filter((r) => {
      if (isClosedForPartsQueue(r)) return false;

      if (!q) return true;

      const advisor = safeString(
        r?.advisorName || r?.serviceAdvisorName || r?.writerName
      );

      const lc =
        typeof getLifecycleFn === "function"
          ? String(getLifecycleFn(r) || "")
          : String(r?.lifecycle || "");

      const effectivePartsStatus = getEffectivePartsStatus(r);

      const blob = [
        r?.roNumber,
        getTag(r),
        r?.customerName,
        r?.vehicle,
        r?.assignedTechName,
        advisor,
        getTransportText(r?.transportType),
        r?.partsRequestType,
        r?.partsRequestNote,
        r?.partsEtaISO,
        r?.partsNotes,
        effectivePartsStatus,
        getEffectiveActionStatus(r),
        r?.notes,
        compactDate(r?.partsActionRequestedAt || r?.timestamps?.partsRequestedAt || r?.createdAt),
        partsActionedDateLabel(r),
        lifecycleLabel({ ...r, lifecycle: lc }, store),
      ]
        .map((x) => safeString(x).toLowerCase())
        .join(" ");

      return blob.includes(q);
    });

    const openQueue = [];
    const actionedQueue = [];

    filtered.forEach((ro) => {
      const partsStatus = getEffectivePartsStatus(ro);
      const actionStatus = getEffectiveActionStatus(ro);

      if (hasActivePartsRequest(ro, partsStatus, actionStatus)) {
        openQueue.push(ro);
        return;
      }

      const hasAction = isActionedPartsRO(partsStatus, actionStatus);

      if (hasAction) {
        if (isWithinDateWindowMs(getPartsActionSortTime(ro), dateWindow)) {
          actionedQueue.push(ro);
        }
      } else {
        openQueue.push(ro);
      }
    });

    return {
      openQueue: openQueue.sort(openSortFn),
      actionedQueue: actionedQueue.sort(actionedSortFn),
    };
  }, [
    ros,
    wipROs,
    isLoading,
    search,
    isWipROFn,
    getLifecycleFn,
    store,
    getEffectivePartsStatus,
    getEffectiveActionStatus,
    dateWindow,
  ]);

  const totalShowing =
    queueData.openQueue.length + queueData.actionedQueue.length;

  const partsOrderedCount = useMemo(() => {
    return [...queueData.openQueue, ...queueData.actionedQueue].filter(
      (ro) => statusUpper(getEffectivePartsStatus(ro)) === "ORDERED"
    ).length;
  }, [queueData.openQueue, queueData.actionedQueue, getEffectivePartsStatus]);

  const oldestReviewLabel = useMemo(
    () => getOldestReviewLabel(queueData.openQueue),
    [queueData.openQueue]
  );

  const threadRO = useMemo(() => {
    if (!threadROId) return null;

    return (
      queueData.openQueue.find((r) => String(r?.id) === String(threadROId)) ||
      queueData.actionedQueue.find((r) => String(r?.id) === String(threadROId)) ||
      (Array.isArray(ros)
        ? ros.find((r) => String(r?.id) === String(threadROId))
        : null) ||
      null
    );
  }, [threadROId, queueData.openQueue, queueData.actionedQueue, ros]);

  const hasUnreadThreadNote = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      return !!(unreadHydrated && unreadStoreMatches && roId && hasUnread(roId));
    },
    [hasUnread, unreadHydrated, unreadStoreMatches]
  );

  const openThreadForRo = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      if (!roId) return;

      setThreadROId(roId);
    },
    []
  );

  const closeThread = useCallback(() => {
    setThreadROId(null);
  }, []);

  const setPartsStatus = async (id, status, meta = {}) => {
    if (typeof setPartsStatusFn !== "function") {
      return alert("Missing setPartsStatus in ROContext.js");
    }

    const roId = safeString(id).trim();
    const nextStatus = safeString(status).trim();

    if (roId && nextStatus) {
      setOptimisticPartsStatusByRO((m) => ({
        ...m,
        [roId]: nextStatus,
      }));
    }

    try {
      await Promise.resolve(setPartsStatusFn(id, status, meta));
      flash(id, `Set to ${String(status).toUpperCase()}.`);
    } catch (err) {
      console.error(err);

      if (roId) {
        setOptimisticPartsStatusByRO((m) => {
          const next = { ...m };
          delete next[roId];
          return next;
        });
      }

      alert(`Unable to update parts status. ${safeString(err?.message).slice(0, 220)}`);
    }
  };

  const setPartsActionStatus = async (id, action, meta = {}) => {
    if (typeof setPartsActionStatusFn !== "function") {
      return alert("Missing setPartsActionStatus in ROContext.js");
    }

    const roId = safeString(id).trim();
    const actionKey = safeString(action).trim().toUpperCase();

    let optimisticStatus = "";
    let optimisticAction = action;

    if (actionKey === "PRE_PULLED" || actionKey === "PREPULLED") {
      optimisticStatus = PARTS_STATUS.PULLED;
      optimisticAction = PARTS_ACTION_STATUS.PRE_PULLED || "PRE_PULLED";
    }

    if (actionKey === "NO_ACTION" || actionKey === "NONE" || actionKey === "CLEAR") {
      optimisticStatus = PARTS_STATUS.NONE;
      optimisticAction = PARTS_ACTION_STATUS.NO_ACTION || "NO_ACTION";
    }

    if (roId && optimisticStatus) {
      setOptimisticPartsStatusByRO((m) => ({
        ...m,
        [roId]: optimisticStatus,
      }));
    }

    if (roId && optimisticAction) {
      setOptimisticActionStatusByRO((m) => ({
        ...m,
        [roId]: optimisticAction,
      }));
    }

    try {
      await Promise.resolve(setPartsActionStatusFn(id, action, meta));

      if (actionKey === "PRE_PULLED" || actionKey === "PREPULLED") {
        flash(id, "Parts pre-pulled.");
      } else if (actionKey === "NO_ACTION") {
        flash(id, "Marked no action.");
      } else {
        flash(id, `Action set to ${actionKey}.`);
      }
    } catch (err) {
      console.error(err);

      if (roId && optimisticStatus) {
        setOptimisticPartsStatusByRO((m) => {
          const next = { ...m };
          delete next[roId];
          return next;
        });
      }

      if (roId && optimisticAction) {
        setOptimisticActionStatusByRO((m) => {
          const next = { ...m };
          delete next[roId];
          return next;
        });
      }

      alert(`Unable to update parts action. ${safeString(err?.message).slice(0, 220)}`);
    }
  };


  const markOrderedWithEta = async (ro) => {
    const roId = safeString(ro?.id).trim();
    if (!roId) return alert("Missing RO id.");

    // ETA is intentionally not gated here.
    // Parts can communicate timing in the shared RO thread using real-world
    // language like "ETA tomorrow morning", "waiting on vendor call back",
    // or "backordered".
    await setPartsStatus(roId, PARTS_STATUS.ORDERED);
  };

  const markQuoted = async (ro) => {
    const reqType = String(ro?.partsRequestType || "").toUpperCase();
    const roId = safeString(ro?.id).trim();

    if (roId) {
      setOptimisticPartsStatusByRO((m) => ({
        ...m,
        [roId]: PARTS_STATUS.QUOTED,
      }));
    }

    try {
      if (typeof updateROFn === "function") {
        const at = nowISO();

        const patch = {
          partsStatus: PARTS_STATUS.QUOTED,
          partsActionStatus: PARTS_ACTION_STATUS.QUOTED || "QUOTED",
          partsStatusUpdatedAt: at,
          partsActionedAt: at,
          timestamps: {
            ...(ro?.timestamps && typeof ro.timestamps === "object"
              ? ro.timestamps
              : {}),
            updatedAt: at,
            partsActionedAt: at,
          },
        };

        if (reqType === "NEED_PRICE") {
          patch.partsRequestType = "";
          patch.partsRequestNote = "";
        }

        await Promise.resolve(
          updateROFn(ro.id, patch, "RO_PARTS_QUOTE_PROVIDED", {
            byRole: actorRole,
            byName: actorName,
          })
        );

        flash(ro.id, "Marked quoted.");
        return;
      }

      await setPartsStatus(ro.id, PARTS_STATUS.QUOTED);

      if (reqType === "NEED_PRICE") {
        if (typeof setPartsRequestType === "function") {
          setPartsRequestType(ro.id, "");
        }
        if (typeof setPartsRequestNote === "function") {
          setPartsRequestNote(ro.id, "");
        }
      }

      flash(ro.id, "Marked quoted.");
    } catch (err) {
      console.error(err);

      if (roId) {
        setOptimisticPartsStatusByRO((m) => {
          const next = { ...m };
          delete next[roId];
          return next;
        });
      }

      alert("Unable to mark quoted.");
    }
  };

  const reopenPartsReview = async (ro) => {
    const roId = safeString(ro?.id).trim();

    if (roId) {
      setOptimisticPartsStatusByRO((m) => ({
        ...m,
        [roId]: PARTS_STATUS.NONE,
      }));
      setOptimisticActionStatusByRO((m) => ({
        ...m,
        [roId]: PARTS_ACTION_STATUS.REVIEW || "REVIEW",
      }));
    }

    if (typeof updateROFn === "function") {
      try {
        await Promise.resolve(
          updateROFn(
            ro.id,
            {
              partsStatus: PARTS_STATUS.NONE,
              partsActionStatus: PARTS_ACTION_STATUS.REVIEW || "REVIEW",
              partsRequestType: "NEED_PARTS",
            },
            "RO_PARTS_REOPENED",
            { byRole: actorRole, byName: actorName }
          )
        );

        flash(ro.id, "Reopened for parts review.");
        return;
      } catch (err) {
        console.error(err);

        if (roId) {
          setOptimisticPartsStatusByRO((m) => {
            const next = { ...m };
            delete next[roId];
            return next;
          });
          setOptimisticActionStatusByRO((m) => {
            const next = { ...m };
            delete next[roId];
            return next;
          });
        }

        alert("Unable to reopen parts review.");
        return;
      }
    }

    if (typeof setPartsStatusFn === "function") {
      try {
        await Promise.resolve(setPartsStatusFn(ro.id, PARTS_STATUS.NONE));
        flash(ro.id, "Reopened for parts review.");
        return;
      } catch (err) {
        console.error(err);

        if (roId) {
          setOptimisticPartsStatusByRO((m) => {
            const next = { ...m };
            delete next[roId];
            return next;
          });
        }

        alert("Unable to reopen parts review.");
        return;
      }
    }

    alert("Missing updateRO in ROContext.js");
  };

  function renderROCard(ro, queueType = "open") {
    const transportText = getTransportText(ro.transportType);
    const effectivePartsStatus = getEffectivePartsStatus(ro);
    const effectiveActionStatus = getEffectiveActionStatus(ro);
    const psP = partsStatusPill(effectivePartsStatus);

    const lcText = lifecycleLabel(ro, store);
    const lcP = lifecyclePill(lcText);

    const reqType = String(ro.partsRequestType || "").toUpperCase();
    const partsStatus = String(effectivePartsStatus || "").toUpperCase();
    const showRequest = !(partsStatus === "QUOTED" && reqType === "NEED_PRICE");
    const reqP = showRequest ? requestPill(ro.partsRequestType) : null;
    const actionP = actionStatusPill(effectiveActionStatus);

    const advisor = safeString(
      ro.advisorName || ro.serviceAdvisorName || ro.writerName || ""
    );

    const tag = getTag(ro);
    const latestNote = notePreview(ro.notes);
    const hasUnreadNote = hasUnreadThreadNote(ro);

    const flags =
      typeof getAttentionFlags === "function" ? getAttentionFlags(ro) : null;

    const attention = [];
    if (flags?.tagMissing) attention.push(attentionPill("Missing Tag"));
    if (flags?.waitingPartsTooLong) attention.push(attentionPill("Parts Delay"));
    if (flags?.promiseOverdue) attention.push(attentionPill("Promise Overdue"));

    const isWaiter = safeString(ro?.transportType).toUpperCase() === "WAITER";
    const isActioned = queueType === "actioned";
    const borderColor = isActioned ? "#dbeafe" : "#fed7aa";
    const leftColor = isActioned ? "#2563eb" : "#f97316";
    const softBg = isActioned ? "#ffffff" : "#fffaf5";

    const notesButton = (
      <button
        type="button"
        onClick={() => openThreadForRo(ro)}
        style={{
          ...actionButtonStyle(hasUnreadNote ? "ordered" : "neutral"),
          position: "relative",
        }}
        title={hasUnreadNote ? "Unread note activity" : "Open notes and activity"}
      >
        Notes / Activity
        {hasUnreadNote ? (
          <span
            style={{
              position: "absolute",
              top: -5,
              right: -5,
              width: 10,
              height: 10,
              borderRadius: 999,
              background: "#f97316",
              border: "2px solid #ffffff",
              boxShadow: "0 0 0 1px rgba(249,115,22,0.18)",
            }}
          />
        ) : null}
      </button>
    );

    const openActions = (
      <div className="parts-queue-actions" style={{ display: "grid", gap: 6 }}>
        <button
          type="button"
          onClick={() =>
            setPartsActionStatus(
              ro.id,
              PARTS_ACTION_STATUS.PRE_PULLED || "PRE_PULLED"
            )
          }
          style={actionButtonStyle("pulled")}
          title="Parts have been pre-pulled"
        >
          Pre-Pulled
        </button>

        <button
          type="button"
          onClick={() => markOrderedWithEta(ro)}
          style={actionButtonStyle("ordered")}
          title="Mark parts ordered. Add ETA/details in the shared note thread when available."
        >
          Ordered
        </button>

        <button
          type="button"
          onClick={() => setPartsStatus(ro.id, PARTS_STATUS.ARRIVED)}
          style={actionButtonStyle("arrived")}
          title="Parts have arrived"
        >
          Arrived
        </button>

        <button
          type="button"
          onClick={() => markQuoted(ro)}
          style={actionButtonStyle("quoted")}
          title="Quote has been provided"
        >
          Quoted
        </button>

        <button
          type="button"
          onClick={() =>
            setPartsActionStatus(
              ro.id,
              PARTS_ACTION_STATUS.NO_ACTION || "NO_ACTION"
            )
          }
          style={actionButtonStyle("neutral")}
          title="No parts action is needed"
        >
          No Action
        </button>

        {notesButton}
      </div>
    );

    const actionedActions = (
      <div className="parts-queue-actions actioned" style={{ display: "grid", gap: 6 }}>
        <button
          type="button"
          onClick={() => reopenPartsReview(ro)}
          style={actionButtonStyle("reopen")}
          title="Move back into Needs Action"
        >
          Reopen
        </button>
        {notesButton}
      </div>
    );

    if (isActioned) {
      return (
        <div
          key={ro.id}
          className="parts-queue-ro is-actioned parts-queue-actioned-card"
          style={{
            ...cardBase,
            background: softBg,
            borderColor,
            borderLeft: `4px solid ${leftColor}`,
          }}
        >
          <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontWeight: 850,
                  fontSize: "0.86rem",
                  color: "#0f172a",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.05,
                }}
              >
                {formatROTitle(ro)}
              </div>

              <span
                style={pillStyle({
                  bg: tag ? "#f8fafc" : "#fee2e2",
                  border: tag ? "#cbd5e1" : "#fca5a5",
                  color: tag ? "#0f172a" : "#991b1b",
                })}
                title={tag ? "Key tag number" : "Missing tag number"}
              >
                {tag ? `Tag #${tag}` : "Tag Missing"}
              </span>

              {lcP ? <span style={lcP.style}>{lcP.text}</span> : null}
              {isWaiter ? <span style={attentionPill("Waiter").style}>Waiter</span> : null}
              {psP ? <span style={psP.style}>{psP.text}</span> : null}
              {actionP ? <span style={actionP.style}>{actionP.text}</span> : null}
              {reqP ? <span style={reqP.style}>{reqP.text}</span> : null}
              {attention.map((p, idx) => (
                <span key={`actioned-attn-${idx}`} style={p.style}>
                  {p.text}
                </span>
              ))}
            </div>

            <div
              style={{
                fontWeight: 750,
                color: "#1f2937",
                fontSize: "0.78rem",
                lineHeight: 1.22,
              }}
            >
              {ro.customerName || "—"}{" "}
              <span style={{ color: "#64748b", fontWeight: 700 }}>—</span>{" "}
              <span style={{ color: "#374151", fontWeight: 650 }}>
                {ro.vehicle || "—"}
              </span>
            </div>

            <div className="parts-queue-meta actioned-meta">
              <span>
                Tech: <b>{ro.assignedTechName || "—"}</b>
              </span>
              {advisor ? (
                <span>
                  Advisor: <b>{advisor}</b>
                </span>
              ) : null}
              <span>
                Review age: <b>{reviewAgeLabel(ro)}</b>
              </span>
              <span>
                Actioned: <b>{partsActionedDateLabel(ro)}</b>
              </span>
              {ro.promiseTimeISO ? (
                <span>
                  Promise: <b>{compactDate(ro.promiseTimeISO)}</b>
                </span>
              ) : null}
              {transportText && transportText !== "Waiter" ? (
                <span>
                  Transport: <b>{transportText}</b>
                </span>
              ) : null}
            </div>

            {safeString(ro.partsRequestNote).trim() ? (
              <div className="parts-queue-note-line">
                <span>Request:</span>{" "}
                {safeString(ro.partsRequestNote).slice(0, 84)}
                {safeString(ro.partsRequestNote).length > 84 ? "…" : ""}
              </div>
            ) : null}

            {latestNote ? (
              <div className="parts-queue-note-line muted">
                <span>Latest:</span> {latestNote}
              </div>
            ) : null}

            {flashByRO[ro.id] ? (
              <div
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 800,
                  color: "#166534",
                }}
              >
                {flashByRO[ro.id]}
              </div>
            ) : null}

            {actionedActions}
          </div>
        </div>
      );
    }

    return (
      <div
        key={ro.id}
        className={`parts-queue-ro ${isActioned ? "is-actioned" : "is-open"}`}
        style={{
          ...cardBase,
          background: softBg,
          borderColor,
          borderLeft: `4px solid ${leftColor}`,
        }}
      >
        <div className="parts-queue-card-main">
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 850,
                  fontSize: "0.9rem",
                  color: "#0f172a",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.05,
                }}
              >
                {formatROTitle(ro)}
              </div>

              <span
                style={pillStyle({
                  bg: tag ? "#f8fafc" : "#fee2e2",
                  border: tag ? "#cbd5e1" : "#fca5a5",
                  color: tag ? "#0f172a" : "#991b1b",
                })}
                title={tag ? "Key tag number" : "Missing tag number"}
              >
                {tag ? `Tag #${tag}` : "Tag Missing"}
              </span>

              {lcP ? <span style={lcP.style}>{lcP.text}</span> : null}
              {isWaiter ? <span style={attentionPill("Waiter").style}>Waiter</span> : null}
              {psP ? <span style={psP.style}>{psP.text}</span> : null}
              {actionP ? <span style={actionP.style}>{actionP.text}</span> : null}
              {reqP ? <span style={reqP.style}>{reqP.text}</span> : null}
              {attention.map((p, idx) => (
                <span key={idx} style={p.style}>
                  {p.text}
                </span>
              ))}
            </div>

            <div
              style={{
                marginTop: 5,
                fontWeight: 700,
                color: "#1f2937",
                fontSize: "0.78rem",
                lineHeight: 1.22,
              }}
            >
              {ro.customerName || "—"}{" "}
              <span style={{ color: "#64748b", fontWeight: 600 }}>—</span>{" "}
              <span style={{ color: "#475569", fontWeight: 650 }}>
                {ro.vehicle || "—"}
              </span>
            </div>

            <div className="parts-queue-meta">
              <span>
                Tech: <b>{ro.assignedTechName || "—"}</b>
              </span>
              {advisor ? (
                <span>
                  Advisor: <b>{advisor}</b>
                </span>
              ) : null}
              <span>
                Review age: <b>{reviewAgeLabel(ro)}</b>
              </span>
              {isActioned ? (
                <span>
                  Actioned: <b>{partsActionedDateLabel(ro)}</b>
                </span>
              ) : null}
              {ro.promiseTimeISO ? (
                <span>
                  Promise: <b>{compactDate(ro.promiseTimeISO)}</b>
                </span>
              ) : null}
              {transportText && transportText !== "Waiter" ? (
                <span>
                  Transport: <b>{transportText}</b>
                </span>
              ) : null}
            </div>

            {safeString(ro.partsRequestNote).trim() ? (
              <div className="parts-queue-note-line">
                <span>Request:</span>{" "}
                {safeString(ro.partsRequestNote).slice(0, isActioned ? 70 : 110)}
                {safeString(ro.partsRequestNote).length > (isActioned ? 70 : 110)
                  ? "…"
                  : ""}
              </div>
            ) : null}

            {latestNote ? (
              <div className="parts-queue-note-line muted">
                <span>Latest:</span> {latestNote}
              </div>
            ) : null}

            {flashByRO[ro.id] ? (
              <div
                style={{
                  marginTop: 5,
                  fontSize: "0.7rem",
                  fontWeight: 800,
                  color: "#166534",
                }}
              >
                {flashByRO[ro.id]}
              </div>
            ) : null}
          </div>

          <div>{isActioned ? actionedActions : openActions}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="parts-queue-page" style={pageWrap}>
      <div className="parts-queue-header" style={headerRow}>
        <div>
          <div style={eyebrowStyle}>Parts Workspace</div>
          <h1 style={pageTitleStyle}>Parts Queue</h1>
          <p style={pageSubtitleStyle}>
            Open parts requests stay visible. Actioned work follows the selected date range.
          </p>
        </div>

        <div className="parts-queue-header-controls" style={headerControls}>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={selectStyle}
            title="Actioned date range"
          >
            <option value={DATE_FILTERS.TODAY}>Actioned: Today</option>
            <option value={DATE_FILTERS.YESTERDAY}>Actioned: Yesterday</option>
            <option value={DATE_FILTERS.THIS_WEEK}>Actioned: This Week</option>
            <option value={DATE_FILTERS.THIS_MONTH}>Actioned: This Month</option>
            <option value={DATE_FILTERS.CUSTOM}>Actioned: Custom</option>
          </select>

          {dateFilter === DATE_FILTERS.CUSTOM ? (
            <>
              <input
                type="date"
                value={customFromDate}
                onChange={(e) => setCustomFromDate(e.target.value)}
                style={dateInputStyle}
              />
              <span style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 800 }}>
                to
              </span>
              <input
                type="date"
                value={customToDate}
                onChange={(e) => setCustomToDate(e.target.value)}
                style={dateInputStyle}
              />
            </>
          ) : null}

          <span
            style={{
              ...pillStyle({
                bg: "#f8fafc",
                border: "#e5e7eb",
                color: "#0f172a",
              }),
              height: 24,
            }}
          >
            Showing: {totalShowing}
          </span>
        </div>
      </div>

      <div className="parts-queue-metrics" style={metricGrid}>
        <div style={metricCardStyle(queueData.openQueue.length ? "warn" : "good")}>
          <div style={metricLabelStyle}>Open Requests</div>
          <div style={metricValueStyle}>{queueData.openQueue.length}</div>
          <div style={metricSubStyle}>Needs parts review.</div>
        </div>

        <div style={metricCardStyle(queueData.actionedQueue.length ? "good" : "neutral")}>
          <div style={metricLabelStyle}>Actioned</div>
          <div style={metricValueStyle}>{queueData.actionedQueue.length}</div>
          <div style={metricSubStyle}>Handled for {dateWindow.label}.</div>
        </div>

        <div style={metricCardStyle(partsOrderedCount ? "warn" : "neutral")}>
          <div style={metricLabelStyle}>Parts Ordered</div>
          <div style={metricValueStyle}>{partsOrderedCount}</div>
          <div style={metricSubStyle}>Open ROs with parts ordered.</div>
        </div>

        <div style={metricCardStyle(queueData.openQueue.length ? "danger" : "good")}>
          <div style={metricLabelStyle}>Oldest Review</div>
          <div style={metricValueStyle}>{oldestReviewLabel}</div>
          <div style={metricSubStyle}>Longest item waiting on Parts.</div>
        </div>
      </div>

      <section className="parts-queue-toolbar" style={toolbarStyle}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search RO, tag, customer, vehicle, advisor, tech, request, notes..."
          style={searchInputStyle}
        />
        <div style={{ color: "#64748b", fontSize: "0.72rem", lineHeight: 1.25 }}>
          Review is live. Actioned work follows the selected date range: <b>{dateWindow.label}</b>.
        </div>
      </section>

      {!hasFreshPartsLoad ? (
        <div style={emptyStateStyle}>Loading latest parts queue…</div>
      ) : totalShowing === 0 ? (
        <div style={emptyStateStyle}>No active parts requests.</div>
      ) : (
        <div className="parts-queue-columns" style={queueColumnsStyle}>
          <section style={columnCard}>
            <div style={sectionHeaderStyle}>
              <div>
                <div style={sectionTitleStyle}>Open Parts Requests</div>
                <div style={sectionSubStyle}>
                  Oldest unactioned first. Waiters and promise risk stay prioritized.
                </div>
              </div>
              <div style={queueCountPillStyle("open")}>
                {queueData.openQueue.length}
              </div>
            </div>

            {queueData.openQueue.length === 0 ? (
              <div style={emptyStateStyle}>No open parts requests.</div>
            ) : (
              <div style={grid}>
                {queueData.openQueue.map((ro) => renderROCard(ro, "open"))}
              </div>
            )}
          </section>

          <section style={columnCard}>
            <div style={sectionHeaderStyle}>
              <div>
                <div style={sectionTitleStyle}>Actioned Requests</div>
                <div style={sectionSubStyle}>Newest action first • {dateWindow.label}</div>
              </div>
              <div style={queueCountPillStyle("actioned")}>
                {queueData.actionedQueue.length}
              </div>
            </div>

            {queueData.actionedQueue.length === 0 ? (
              <div style={emptyStateStyle}>No actioned parts requests for {dateWindow.label}.</div>
            ) : (
              <div style={grid}>
                {queueData.actionedQueue.map((ro) => renderROCard(ro, "actioned"))}
              </div>
            )}
          </section>
        </div>
      )}

      <ROActivityDrawer
        ro={threadRO}
        isOpen={!!threadRO}
        onClose={closeThread}
        currentUser={currentUser}
        actorRole={actorRole}
        actorName={actorName}
        title="Parts RO Thread"
      />

      <style>
        {`
          .parts-queue-card-main {
            display: grid;
            grid-template-columns: minmax(0, 1fr) 306px;
            gap: 9px;
            align-items: center;
          }

          .parts-queue-actions {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .parts-queue-actions.actioned {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .parts-queue-actions button {
            width: 100%;
            min-width: 0;
          }

          .parts-queue-actioned-card {
            overflow-wrap: normal;
            word-break: normal;
          }

          .parts-queue-actioned-card .parts-queue-meta {
            display: grid;
            grid-template-columns: 1fr;
            gap: 2px;
          }

          .parts-queue-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 3px 10px;
            margin-top: 4px;
            color: #64748b;
            font-size: 0.7rem;
            line-height: 1.24;
          }

          .parts-queue-meta b {
            color: #334155;
            font-weight: 750;
          }

          .parts-queue-note-line {
            margin-top: 5px;
            font-size: 0.7rem;
            color: #64748b;
            background: #f8fafc;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 4px 6px;
            line-height: 1.25;
          }

          .parts-queue-note-line span {
            font-weight: 850;
            color: #475569;
          }

          .parts-queue-note-line.muted {
            background: #ffffff;
          }

          @media (max-width: 1180px) {
            .parts-queue-page {
              padding: 8px !important;
            }

            .parts-queue-columns {
              grid-template-columns: 1fr !important;
            }

            .parts-queue-card-main {
              grid-template-columns: 1fr !important;
            }

            .parts-queue-header {
              align-items: flex-start !important;
            }

            .parts-queue-header-controls {
              width: 100%;
              margin-left: 0 !important;
              justify-content: flex-start !important;
            }

            .parts-queue-header-controls select,
            .parts-queue-header-controls input {
              min-width: 0 !important;
              flex: 1 1 170px;
            }
          }

          @media (max-width: 1050px) {
            .parts-queue-metrics {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .parts-queue-actions {
              grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            }

            .parts-queue-actions.actioned {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 900px) {
            .parts-queue-page {
              padding: 4px !important;
              gap: 8px !important;
            }

            .parts-queue-actions {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .parts-queue-toolbar {
              padding: 0.55rem 0.6rem !important;
            }
          }

          @media (max-width: 560px) {
            .parts-queue-metrics,
            .parts-queue-actions,
            .parts-queue-actions.actioned {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}
